import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart2,
  Clock,
  RefreshCw,
  Search,
  TrendingUp,
  Users
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const ACTIVITY_LIMIT = 25;
const MEMBERS_LIMIT = 25;

/**
 * Pagina dedicata al dettaglio di una palestra: tab Panoramica, Attività, Membri, Insights.
 * Accesso diretto via URL /admin/gyms/:gymId — niente "seleziona poi vai in Attività".
 */
const AdminGymDetailPage = () => {
  const { gymId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  const { t } = useTranslation();

  const gymNameFromState = location.state?.gymName || null;

  const [gymInfo, setGymInfo] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsError, setDetailsError] = useState(null);

  const [activityData, setActivityData] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState(null);

  const [membersData, setMembersData] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState(null);
  const [membersPage, setMembersPage] = useState(1);

  const [memberFilters, setMemberFilters] = useState({ status: 'all', role: 'member', search: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const formatNumber = useCallback((value, options = {}) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('it-IT', {
      maximumFractionDigits: options.maxDecimals ?? 0,
      minimumFractionDigits: options.minDecimals ?? 0
    }).format(value);
  }, []);

  const formatPercentage = useCallback((value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('it-IT', {
      style: 'percent',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0
    }).format(value / 100);
  }, []);

  const formatDateTime = useCallback((value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  }, []);

  const fetchGymInfo = useCallback(async () => {
    if (!api || !gymId) return;
    try {
      const res = await api.get('/admin_gym_dashboard.php', { params: { action: 'overview_list' } });
      const gyms = res.data?.data?.gyms ?? [];
      const gym = gyms.find((g) => String(g.gym_id) === String(gymId));
      setGymInfo(gym || null);
    } catch (e) {
      console.error(e);
    }
  }, [api, gymId]);

  const fetchDetails = useCallback(async () => {
    if (!api || !gymId) return;
    setDetailsLoading(true);
    setDetailsError(null);
    try {
      const res = await api.get('/admin_gym_dashboard.php', {
        params: { action: 'gym_details', gym_id: gymId, months: 6 }
      });
      setDetailsData(res.data?.data ?? null);
    } catch (error) {
      setDetailsError(t('admin.gymMonitor.errors.details'));
    } finally {
      setDetailsLoading(false);
    }
  }, [api, gymId, t]);

  const fetchActivity = useCallback(async () => {
    if (!api || !gymId) return;
    setActivityLoading(true);
    setActivityError(null);
    try {
      const res = await api.get('/admin_gym_dashboard.php', {
        params: { action: 'activity_feed', gym_id: gymId, limit: ACTIVITY_LIMIT, page: 1 }
      });
      setActivityData(res.data?.data?.events ?? []);
    } catch (error) {
      setActivityError(t('admin.gymMonitor.errors.activity'));
    } finally {
      setActivityLoading(false);
    }
  }, [api, gymId, t]);

  const fetchMembers = useCallback(async () => {
    if (!api || !gymId) return;
    setMembersLoading(true);
    setMembersError(null);
    try {
      const params = { action: 'members', gym_id: gymId, limit: MEMBERS_LIMIT, page: membersPage };
      if (memberFilters.status !== 'all') params.status = memberFilters.status;
      if (memberFilters.role !== 'all') params.role = memberFilters.role;
      if (memberFilters.search) params.search = memberFilters.search;
      const res = await api.get('/admin_gym_dashboard.php', { params });
      const payload = res.data?.data ?? {};
      setMembersData(payload.members ?? []);
      setMembersTotal(payload.total ?? 0);
    } catch (error) {
      setMembersError(t('admin.gymMonitor.errors.members'));
    } finally {
      setMembersLoading(false);
    }
  }, [api, gymId, membersPage, memberFilters, t]);

  useEffect(() => {
    if (!gymId) return;
    if (!gymNameFromState) fetchGymInfo();
  }, [gymId, gymNameFromState, fetchGymInfo]);

  useEffect(() => {
    if (!gymId) return;
    fetchDetails();
    fetchActivity();
    fetchMembers();
  }, [gymId, fetchDetails, fetchActivity, fetchMembers]);

  useEffect(() => {
    if (!gymId) return;
    fetchMembers();
  }, [gymId, membersPage, memberFilters, fetchMembers]);

  useEffect(() => {
    const t = setTimeout(() => {
      setMemberFilters((prev) => ({ ...prev, search: searchTerm.trim() }));
      setMembersPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const displayName = gymNameFromState || gymInfo?.name || t('admin.gymDetail.gym');

  const kpiCards = useMemo(() => {
    if (!detailsData?.overview) return [];
    const o = detailsData.overview;
    return [
      { key: 'totalMembers', label: t('admin.gymMonitor.details.kpi.totalMembers'), value: formatNumber(o.total_members) },
      { key: 'newThisMonth', label: t('admin.gymMonitor.details.kpi.newThisMonth'), value: formatNumber(o.new_this_month) },
      { key: 'newDelta', label: t('admin.gymMonitor.details.kpi.newDelta'), value: formatNumber(o.new_delta, { maxDecimals: 0 }) },
      { key: 'workoutsThisMonth', label: t('admin.gymMonitor.details.kpi.workoutsThisMonth'), value: formatNumber(o.workouts_this_month) },
      { key: 'activeCourses', label: t('admin.gymMonitor.details.kpi.activeCourses'), value: formatNumber(o.active_courses) },
      { key: 'retentionRate', label: t('admin.gymMonitor.details.kpi.retentionRate'), value: formatPercentage(o.retention_rate) },
      { key: 'avgVisitsPerMember', label: t('admin.gymMonitor.details.kpi.avgVisitsPerMember'), value: formatNumber(o.avg_visits_per_member, { maxDecimals: 2 }) }
    ];
  }, [detailsData, formatNumber, formatPercentage, t]);

  const frequencyDistribution = useMemo(() => {
    const dist = detailsData?.frequency_distribution?.distribution ?? {};
    return Object.entries(dist).map(([category, values]) => ({
      category,
      count: values.count ?? 0,
      percentage: values.percentage ?? 0
    }));
  }, [detailsData]);

  const membersMaxPage = Math.max(1, Math.ceil(membersTotal / MEMBERS_LIMIT));

  const handleRefresh = () => {
    fetchDetails();
    fetchActivity();
    fetchMembers();
  };

  const tabClass =
    'flex items-center gap-2 rounded-xl border border-transparent px-4 py-2 text-sm font-medium text-gray-500 transition-all hover:text-gray-700 dark:hover:text-gray-200 data-[state=active]:border-blue-500/70 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg whitespace-nowrap';

  if (!gymId) {
    return (
      <div className="p-6">
        <button type="button" onClick={() => navigate(-1)} className="text-blue-600 dark:text-blue-400 flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> {t('admin.gymDetail.backToList')}
        </button>
        <p className="mt-4 text-gray-500">{t('admin.gymDetail.invalidGym')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header con breadcrumb e nome palestra */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('admin.gymDetail.backToList')}
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{displayName}</h1>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 transition"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.refresh')}
          </button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap gap-2 p-2 bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
          <TabsTrigger value="overview" className={tabClass}>
            <BarChart2 className="h-4 w-4" />
            {t('admin.gymMonitor.sections.overview')}
          </TabsTrigger>
          <TabsTrigger value="activity" className={tabClass}>
            <Activity className="h-4 w-4" />
            {t('admin.gymMonitor.sections.activity')}
          </TabsTrigger>
          <TabsTrigger value="members" className={tabClass}>
            <Users className="h-4 w-4" />
            {t('admin.gymMonitor.sections.members')}
          </TabsTrigger>
          <TabsTrigger value="insights" className={tabClass}>
            <TrendingUp className="h-4 w-4" />
            {t('admin.gymMonitor.sections.insights')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800 overflow-hidden">
            {detailsLoading ? (
              <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
            ) : detailsError ? (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 p-6">
                <AlertTriangle className="h-5 w-5" /><span>{detailsError}</span>
              </div>
            ) : !detailsData ? (
              <p className="p-6 text-gray-500">—</p>
            ) : (
              <div className="p-6 space-y-6">
                {gymInfo && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('admin.gymMonitor.details.contact.title')}</h3>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.details.contact.email')}</dt><dd className="text-gray-900 dark:text-gray-100">{gymInfo.email || '—'}</dd></div>
                        <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.details.contact.phone')}</dt><dd className="text-gray-900 dark:text-gray-100">{gymInfo.phone || '—'}</dd></div>
                        <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.details.contact.address')}</dt><dd className="max-w-[65%] text-right text-gray-900 dark:text-gray-100">{gymInfo.address || '—'}</dd></div>
                      </dl>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('admin.gymMonitor.details.contact.capacityTitle')}</h3>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.details.contact.maxUsers')}</dt><dd className="text-gray-900 dark:text-gray-100">{gymInfo.max_users ? formatNumber(gymInfo.max_users) : '—'}</dd></div>
                        <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.details.contact.maxTrainers')}</dt><dd className="text-gray-900 dark:text-gray-100">{gymInfo.max_trainers ? formatNumber(gymInfo.max_trainers) : '—'}</dd></div>
                      </dl>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  {kpiCards.map((kpi) => (
                    <div key={kpi.key} className="bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-2">{kpi.label}</p>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{kpi.value}</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4"><Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" /><h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('admin.gymMonitor.details.todayStats.title')}</h3></div>
                    <dl className="space-y-3">
                      <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.details.todayStats.workouts')}</dt><dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatNumber(detailsData.today_stats?.workouts_today)}</dd></div>
                      <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.details.todayStats.sessions')}</dt><dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatNumber(detailsData.today_stats?.sessions_today)}</dd></div>
                      <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.details.todayStats.activeTrainers')}</dt><dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatNumber(detailsData.today_stats?.active_trainers)}</dd></div>
                    </dl>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /><h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('admin.gymMonitor.details.memberTrend')}</h3></div>
                    <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                      {(detailsData.member_trend ?? []).map((item) => (
                        <div key={item.month} className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-300">{item.month_label}</span>
                          <span className="text-gray-900 dark:text-gray-100 font-medium">{formatNumber(item.total_active)} <span className="text-xs text-gray-400">+{formatNumber(item.new_members)}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4"><BarChart2 className="h-5 w-5 text-purple-600 dark:text-purple-400" /><h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('admin.gymMonitor.details.workoutTrend')}</h3></div>
                    <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                      {(detailsData.workout_trend ?? []).map((item) => (
                        <div key={item.month} className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-300">{item.month_label}</span>
                          <span className="text-gray-900 dark:text-gray-100 font-medium">{formatNumber(item.workouts_count)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('admin.gymMonitor.activity.title')}</h3>
              </div>
              <button type="button" onClick={() => fetchActivity()} className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition">
                <RefreshCw className="h-4 w-4 mr-1.5" /> {t('common.refresh')}
              </button>
            </div>
            <div className="max-h-[28rem] overflow-y-auto">
              {activityLoading ? <div className="flex justify-center py-8"><LoadingSpinner /></div> : activityError ? (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 p-6"><AlertTriangle className="h-5 w-5" /><span>{activityError}</span></div>
              ) : activityData.length === 0 ? (
                <p className="p-6 text-sm text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.activity.empty')}</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {activityData.map((event) => (
                    <li key={`${event.type}-${event.event_id}`} className="p-6">
                      <div className="flex justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t(`admin.gymMonitor.activity.types.${event.type}`, event.type)}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{event.actor_name || '-'} {event.actor_email && <span className="text-xs text-gray-400 ml-2">{event.actor_email}</span>}</p>
                        </div>
                        <span className="text-xs text-gray-400">{formatDateTime(event.timestamp)}</span>
                      </div>
                      {event.metric_value != null && <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{formatNumber(event.metric_value)}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('admin.gymMonitor.members.title')}</h3>
                <button type="button" onClick={() => fetchMembers()} className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition">
                  <RefreshCw className="h-4 w-4 mr-1.5" /> {t('common.refresh')}
                </button>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-1 bg-gray-50 dark:bg-gray-800/40">{formatNumber(membersTotal)} {t('admin.gymMonitor.members.total')}</span>
                <span className="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-1 bg-gray-50 dark:bg-gray-800/40">{formatNumber(membersData.filter((m) => m.status === 'active').length)} {t('admin.gymMonitor.members.activeCount')}</span>
              </div>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute top-1/2 left-3 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm" placeholder={t('admin.gymMonitor.members.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
            </div>
            <div className="overflow-x-auto">
              {membersLoading ? <div className="flex justify-center py-8"><LoadingSpinner /></div> : membersError ? (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 p-6"><AlertTriangle className="h-5 w-5" /><span>{membersError}</span></div>
              ) : membersData.length === 0 ? (
                <p className="p-6 text-sm text-gray-500 dark:text-gray-400">{t('admin.gymMonitor.members.empty')}</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800/30">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('admin.gymMonitor.members.table.name')}</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('admin.gymMonitor.members.table.email')}</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{t('admin.gymMonitor.members.table.role')}</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{t('admin.gymMonitor.members.table.status')}</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{t('admin.gymMonitor.members.table.joined')}</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{t('admin.gymMonitor.members.table.lastWorkout')}</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">{t('admin.gymMonitor.members.table.workouts30d')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
                    {membersData.map((member) => (
                      <tr key={member.id}>
                        <td className="px-6 py-4"><p className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.name || '-'}</p><p className="text-xs text-gray-400">{member.username}</p></td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{member.email || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 uppercase">{member.role_in_gym || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 uppercase">{member.status || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{member.joined_at ? formatDateTime(member.joined_at) : '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{member.last_workout_at ? formatDateTime(member.last_workout_at) : '—'}</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-600 dark:text-gray-300">{formatNumber(member.workouts_30d)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {membersTotal > 0 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-300">
                <span>{(membersPage - 1) * MEMBERS_LIMIT + 1}-{Math.min(membersPage * MEMBERS_LIMIT, membersTotal)} / {membersTotal}</span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setMembersPage((p) => Math.max(1, p - 1))} disabled={membersPage === 1} className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm disabled:opacity-40">‹</button>
                  <span>{membersPage}/{membersMaxPage}</span>
                  <button type="button" onClick={() => setMembersPage((p) => Math.min(membersMaxPage, p + 1))} disabled={membersPage >= membersMaxPage} className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm disabled:opacity-40">›</button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="insights" className="mt-4">
          <div className="space-y-6">
            {!detailsData ? (
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-100 dark:border-gray-800 p-6"><p className="text-sm text-gray-500">—</p></div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4">
                    <div className="flex items-center gap-2 mb-4"><Users className="h-5 w-5 text-amber-600 dark:text-amber-400" /><h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('admin.gymMonitor.details.frequencyDistribution')}</h3></div>
                    <ul className="space-y-2 text-sm">
                      {frequencyDistribution.map((item) => (
                        <li key={item.category} className="flex justify-between text-gray-700 dark:text-gray-300">
                          <span>{item.category}</span>
                          <span className="font-medium">{formatNumber(item.count)} <span className="text-xs text-gray-400">({formatNumber(item.percentage, { maxDecimals: 1 })}%)</span></span>
                        </li>
                      ))}
                      {frequencyDistribution.length === 0 && <li className="text-sm text-gray-500">-</li>}
                    </ul>
                  </div>
                  <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4">
                    <div className="flex items-center gap-2 mb-4"><Clock className="h-5 w-5 text-rose-600 dark:text-rose-400" /><h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('admin.gymMonitor.details.atRiskMembers')}</h3></div>
                    <div className="space-y-2 max-h-48 overflow-y-auto text-sm">
                      {(detailsData.at_risk_members ?? []).map((member) => (
                        <div key={member.id} className="flex justify-between">
                          <div><p className="font-medium text-gray-700 dark:text-gray-200">{member.name}</p><p className="text-xs text-gray-500">{member.email}</p></div>
                          <div className="text-right"><p className="font-medium">{formatNumber(member.days_inactive)} <span className="text-xs text-gray-400">d</span></p><p className="text-xs text-gray-500">{member.last_workout_date ? formatDateTime(member.last_workout_date) : '—'}</p></div>
                        </div>
                      ))}
                      {(detailsData.at_risk_members ?? []).length === 0 && <p className="text-sm text-gray-500">-</p>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('admin.gymMonitor.details.topCourses')}</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto text-sm">
                      {(detailsData.top_courses ?? []).map((course) => (
                        <div key={course.id} className="flex justify-between">
                          <div><p className="font-medium text-gray-800 dark:text-gray-100">{course.title}</p><p className="text-xs text-gray-500">{course.category}</p></div>
                          <div className="text-right"><p className="font-medium">{formatNumber(course.total_enrollments)} <span className="text-xs text-gray-400">enr.</span></p><p className="text-xs text-gray-500">{formatNumber(course.avg_occupation_pct, { maxDecimals: 1 })}%</p></div>
                        </div>
                      ))}
                      {(detailsData.top_courses ?? []).length === 0 && <p className="text-sm text-gray-500">-</p>}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('admin.gymMonitor.details.topMembers')}</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto text-sm">
                      {(detailsData.top_members ?? []).map((member) => (
                        <div key={member.id} className="flex justify-between items-center">
                          <div><p className="font-medium text-gray-800 dark:text-gray-100">#{member.rank} {member.name}</p><p className="text-xs text-gray-500">{member.email}</p></div>
                          <div className="text-right"><p className="font-medium">{formatNumber(member.workout_days)} <span className="text-xs text-gray-400">days</span></p><p className="text-xs text-gray-500">{formatNumber(member.total_workouts)} workouts</p></div>
                        </div>
                      ))}
                      {(detailsData.top_members ?? []).length === 0 && <p className="text-sm text-gray-500">-</p>}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminGymDetailPage;
